import { localId } from '@/lib/appStorage';

export interface Card {
  id: string;
  question: string;
  answer: string;
  explanation: string;
  level: number;
  nextReview: string;
  right: number;
  wrong: number;
}

export interface Deck {
  id: string;
  name: string;
  subject: string;
  chapter: string;
  cards: Card[];
  /** Origine du jeu : manuel, notes ou document. */
  origin?: 'manual' | 'notes' | 'document';
}

export interface QuizResult {
  id: string;
  deckId: string;
  deckName: string;
  mode: 'quiz' | 'exam';
  score: number;
  total: number;
  at: string;
}

export interface FlashcardsState {
  decks: Deck[];
  results: QuizResult[];
  xp: number;
  streak: number;
  lastPlayed: string;
  dailyGoal: number;
}

export interface QuizQuestion {
  cardId: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export const INTERVALS = [1, 2, 4, 7, 15, 30];

export const todayIso = () => new Date().toISOString().slice(0, 10);

export const EMPTY_CARDS_STATE: FlashcardsState = {
  decks: [],
  results: [],
  xp: 0,
  streak: 0,
  lastPlayed: '',
  dailyGoal: 5,
};

export const makeCard = (question: string, answer: string, explanation = ''): Card => ({
  id: localId('card'),
  question,
  answer,
  explanation,
  level: 0,
  nextReview: todayIso(),
  right: 0,
  wrong: 0,
});

export function shuffle<T>(array: T[]): T[] {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export function buildQuiz(deck: Deck, limit: number): QuizQuestion[] {
  const cards = shuffle(deck.cards).slice(0, limit);
  return cards.map((card) => {
    const candidates = deck.cards.filter((c) => c.id !== card.id && c.answer.trim()).map((c) => c.answer);
    const distractors = shuffle(candidates).slice(0, 3);
    const options = shuffle([card.answer, ...distractors]);
    return {
      cardId: card.id,
      question: card.question,
      options,
      correctIndex: Math.max(0, options.indexOf(card.answer)),
      explanation: card.explanation || card.answer,
    };
  });
}

/** Niveau Duolingo : chaque niveau demande 60 XP de plus. */
export function levelFromXp(xp: number): { level: number; current: number; next: number; progress: number } {
  let level = 1;
  let remaining = xp;
  let needed = 60;
  while (remaining >= needed) {
    remaining -= needed;
    level += 1;
    needed += 30;
  }
  return { level, current: remaining, next: needed, progress: Math.round((remaining / needed) * 100) };
}

/** Série : +1 si la dernière partie date d'hier, remise à 1 si plus ancien. */
export function nextStreak(lastPlayed: string, streak: number): number {
  const today = todayIso();
  if (lastPlayed === today) return Math.max(1, streak);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const iso = yesterday.toISOString().slice(0, 10);
  return lastPlayed === iso ? streak + 1 : 1;
}